import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../../../utils/api';
import { toLocalDateString } from '../../../../utils/dateUtils';
import Pagination, { PAGE_SIZE } from '../../../../components/portal/Pagination';
import RichEditor from '../../../../components/portal/RichEditor';
import {
  RiAddLine, RiFileList3Line, RiTimeLine,
  RiEditLine, RiDeleteBinLine, RiSaveLine,
  RiCloseLine, RiRefreshLine, RiCalendarLine,
  RiFilterLine,
} from 'react-icons/ri';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

// Display helper: shows only the sections (Completed / In Progress / Notes) that have content.
// Values are HTML produced by the rich editor; falls back to legacy plain `content` field for older records.
function UpdateSections({ u }) {
  const completed   = u.completed   || '';
  const inProgress  = u.in_progress || u.inProgress || '';
  const notes       = u.notes       || '';
  const legacy      = u.content     || u.text       || '';
  const isHTMLEmpty = (s) => !s || String(s).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length === 0;
  const hasNew      = !isHTMLEmpty(completed) || !isHTMLEmpty(inProgress) || !isHTMLEmpty(notes);

  if (!hasNew) {
    return (
      <p className="text-[#1F2937] text-sm leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
        {legacy || '—'}
      </p>
    );
  }

  const Section = ({ color, label, value }) => {
    if (isHTMLEmpty(value)) return null;
    return (
      <div className="flex gap-2.5">
        <div className="w-1 rounded-full flex-shrink-0" style={{ background: color }} />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color }}>{label}</p>
          <div
            className="rich-content text-[#1F2937] text-sm leading-relaxed break-words [overflow-wrap:anywhere]"
            dangerouslySetInnerHTML={{ __html: value }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2.5">
      <Section color="#10b981" label="Completed"   value={completed} />
      <Section color="#0984e3" label="In Progress" value={inProgress} />
      <Section color="#7c3aed" label="Notes"       value={notes} />
    </div>
  );
}

export default function DailyUpdatesTab() {
  const today = new Date();

  // ── form state ────────────────────────────────────────────────────────────
  const [date,       setDate]       = useState(toLocalDateString(today));
  // Three independent fields — at least one must be filled to submit
  const [completed,   setCompleted]   = useState('');
  const [inProgress,  setInProgress]  = useState('');
  const [notes,       setNotes]       = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [formError,   setFormError]   = useState('');

  // ── list / filter state ───────────────────────────────────────────────────
  const [updates,    setUpdates]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [listError,  setListError]  = useState('');
  // Default fallback when no specific date is selected — current month.
  const filterMonth = today.getMonth() + 1;
  const filterYear  = today.getFullYear();
  // When set (YYYY-MM-DD), filters to that specific day; when empty, falls back to month/year.
  const [filterDate,  setFilterDate]  = useState('');

  // ── edit / delete state ───────────────────────────────────────────────────
  // When editingId is set, the top "Submit Update" form switches into edit mode
  // (pre-filled, button becomes "Save Changes", PUT instead of POST).
  const [editingId,  setEditingId]  = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const formCardRef = useRef(null);

  // ── pagination ────────────────────────────────────────────────────────────
  const [page,  setPage]  = useState(1);
  const [total, setTotal] = useState(0);

  // ── load with current filter ──────────────────────────────────────────────
  const load = useCallback(() => {
    setLoading(true);
    setListError('');
    const params = { page, limit: PAGE_SIZE };
    if (filterDate) {
      params.date = filterDate;
    } else {
      params.month = filterMonth;
      params.year = filterYear;
    }
    api.get('/daily-updates/mine', { params })
      .then((res) => {
        const d = res.data;
        const list = Array.isArray(d) ? d : d?.updates || d?.data || [];
        setUpdates(list);
        setTotal(d?.meta?.total ?? list.length);
      })
      .catch(() => setListError('Failed to load updates. Please try again.'))
      .finally(() => setLoading(false));
  }, [filterMonth, filterYear, filterDate, page]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when filter changes
  useEffect(() => { setPage(1); }, [filterMonth, filterYear, filterDate]);

  // Submit is enabled only when at least one of the 3 fields has real (non-empty) content.
  // RichEditor produces "<p><br></p>" when empty, so use the helper to strip that out.
  const canSubmit = !RichEditor.isEmpty(completed) || !RichEditor.isEmpty(inProgress) || !RichEditor.isEmpty(notes);

  const buildPayload = (c, ip, n, d) => {
    const p = { date: d };
    if (!RichEditor.isEmpty(c))  p.completed   = c;
    if (!RichEditor.isEmpty(ip)) p.in_progress = ip;
    if (!RichEditor.isEmpty(n))  p.notes       = n;
    return p;
  };

  const resetForm = () => {
    setCompleted('');
    setInProgress('');
    setNotes('');
    setDate(toLocalDateString(today));
    setEditingId(null);
    setFormError('');
  };

  // ── load an existing update into the top form for editing ────────────────
  const startEdit = (u) => {
    const id = u._id || u.id;
    setEditingId(id);
    setCompleted(u.completed   || '');
    setInProgress(u.in_progress || u.inProgress || '');
    setNotes(u.notes           || '');
    const raw = u.date || u.createdAt;
    setDate(raw ? toLocalDateString(new Date(raw)) : toLocalDateString(today));
    setFormError('');
    // Scroll the form into view so the user immediately sees the pre-filled state
    setTimeout(() => {
      formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const cancelEdit = () => resetForm();

  // ── submit: POST on create, PUT on edit ──────────────────────────────────
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setFormError('');
    try {
      const payload = buildPayload(completed, inProgress, notes, date);

      if (editingId) {
        const res = await api.put(`/daily-updates/${editingId}`, payload);
        const updated = res.data?.update || res.data?.data || res.data;
        setUpdates((prev) =>
          prev.map((u) => (u._id || u.id) === editingId
            ? (updated && typeof updated === 'object' ? updated : { ...u, ...payload })
            : u)
        );
      } else {
        const res = await api.post('/daily-updates', payload);
        const created = res.data?.update || res.data?.data || res.data;
        // only prepend to list if it belongs to the currently viewed month
        const d = new Date(date);
        if (d.getMonth() + 1 === filterMonth && d.getFullYear() === filterYear) {
          setUpdates((prev) => [created, ...prev]);
        }
      }
      resetForm();
    } catch (err) {
      setFormError(err.response?.data?.message || (editingId ? 'Failed to update.' : 'Failed to submit update.'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── delete helper ─────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/daily-updates/${id}`);
      setUpdates((prev) => prev.filter((u) => (u._id || u.id) !== id));
    } catch (err) {
      setListError(err.response?.data?.message || 'Failed to delete.');
    } finally {
      setDeletingId(null);
    }
  };

  // ── display helpers ───────────────────────────────────────────────────────
  const formatDate = (u) => {
    const raw = u.date || u.createdAt || u.updatedAt;
    if (!raw) return '';
    const d = new Date(raw);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isToday = (u) => {
    const raw = u.date || u.createdAt;
    return raw && new Date(raw).toDateString() === today.toDateString();
  };

  // group updates by date label
  const grouped = updates.reduce((acc, u) => {
    const raw = u.date || u.createdAt;
    const label = raw
      ? new Date(raw).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })
      : 'Unknown date';
    if (!acc[label]) acc[label] = [];
    acc[label].push(u);
    return acc;
  }, {});

  const isFormToday = date === toLocalDateString(today);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">

      {/* ── LEFT: Submit / Edit form ──────────────────────────────────────── */}
      <div className="xl:col-span-2 space-y-4">
        <div
          ref={formCardRef}
          className={`bg-white border rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden transition-colors ${
            editingId ? 'border-[#0984e3]' : 'border-[#E5E7EB]'
          }`}
        >

          {/* card header */}
          <div className="px-6 py-4 border-b border-[#F3F4F6]" style={{ background: editingId
            ? 'linear-gradient(135deg,rgba(9,132,227,0.08),rgba(213,0,249,0.08))'
            : 'linear-gradient(135deg,rgba(9,132,227,0.04),rgba(213,0,249,0.04))' }}>
            <h3 className="text-[#1F2937] font-bold flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#0984e3,#d500f9)' }}>
                {editingId ? <RiEditLine size={13} className="text-white" /> : <RiAddLine size={14} className="text-white" />}
              </div>
              {editingId ? 'Edit Update' : 'Submit Update'}
            </h3>
            <p className="text-[#9CA3AF] text-xs mt-1">
              {editingId
                ? 'Editing existing update — changes will be saved when you click Save Changes.'
                : 'What did you work on? Share blockers and plans.'}
            </p>
          </div>

          <div className="p-6 space-y-4">

            {formError && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center justify-between">
                {formError}
                <button onClick={() => setFormError('')} className="cursor-pointer ml-2 flex-shrink-0"><RiCloseLine size={14} /></button>
              </div>
            )}

            {/* Date picker + quick-date shortcuts */}
            <div>
              <label className="block text-xs font-semibold text-[#6B7280] mb-1.5 uppercase tracking-wide flex items-center gap-1">
                <RiCalendarLine size={12} /> Date
                {isFormToday && (
                  <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-[#10b981] bg-[rgba(16,185,129,0.1)] normal-case tracking-normal">Today</span>
                )}
              </label>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {[
                  { label: 'Today',     offset: 0 },
                  { label: 'Yesterday', offset: -1 },
                ].map(({ label, offset }) => {
                  const d = new Date(today); d.setDate(d.getDate() + offset);
                  const val = toLocalDateString(d);
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setDate(val)}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                        date === val
                          ? 'border-[#0984e3] text-[#0984e3] bg-[rgba(9,132,227,0.06)]'
                          : 'border-[#E5E7EB] text-[#6B7280] hover:border-[#0984e3] hover:text-[#0984e3]'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <input
                type="date"
                value={date}
                max={toLocalDateString(today)}
                onChange={(e) => setDate(e.target.value)}
                className="input-field"
              />
            </div>

            {/* Completed */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-[#10b981] mb-1.5 uppercase tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10b981]" />
                Completed
              </label>
              <RichEditor
                value={completed}
                onChange={setCompleted}
                placeholder="What did you finish today?"
                accentColor="#10b981"
              />
            </div>

            {/* In progress */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-[#0984e3] mb-1.5 uppercase tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0984e3]" />
                In Progress
              </label>
              <RichEditor
                value={inProgress}
                onChange={setInProgress}
                placeholder="What are you currently working on?"
                accentColor="#0984e3"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-[#7c3aed] mb-1.5 uppercase tracking-wide">
                <span className="w-1.5 h-1.5 rounded-full bg-[#7c3aed]" />
                Notes
              </label>
              <RichEditor
                value={notes}
                onChange={setNotes}
                placeholder="Blockers, questions, or anything else…"
                accentColor="#7c3aed"
              />
            </div>

            <p className="text-[11px] text-[#9CA3AF]">
              Fill in at least one of the three sections above to submit.
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
                className="btn btn-primary flex-1 justify-center flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ padding: '0.65rem 1.2rem', fontSize: '13px' }}
              >
                {submitting
                  ? <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                  : (editingId ? <RiSaveLine size={15} /> : <RiAddLine size={15} />)}
                {submitting
                  ? (editingId ? 'Saving…' : 'Submitting…')
                  : (editingId ? 'Save Changes' : `Submit${isFormToday ? " Today's" : ''} Update`)}
              </button>
              {editingId && (
                <button
                  onClick={cancelEdit}
                  disabled={submitting}
                  className="btn flex items-center gap-1.5 text-[#6B7280] border border-[#E5E7EB] hover:bg-[#F8FAFC] disabled:opacity-50"
                  style={{ padding: '0.65rem 1rem', fontSize: '13px' }}
                >
                  <RiCloseLine size={14} /> Cancel
                </button>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* ── RIGHT: Updates list with month filter ─────────────────────────── */}
      <div className="xl:col-span-3 bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden flex flex-col">

        {/* list header + filter */}
        <div className="px-6 py-4 border-b border-[#F3F4F6]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[rgba(9,132,227,0.08)] flex items-center justify-center">
                <RiFilterLine size={14} className="text-[#0984e3]" />
              </div>
              <div>
                <h3 className="text-[#1F2937] font-bold text-sm">My Updates</h3>
                <p className="text-[#9CA3AF] text-xs">
                  {loading
                    ? 'Loading…'
                    : `${total} update${total !== 1 ? 's' : ''} — ${
                        filterDate
                          ? new Date(filterDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                          : `${MONTH_NAMES[filterMonth - 1]} ${filterYear}`
                      }`}
                </p>
              </div>
            </div>

            {/* Date picker (full calendar dropdown) */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="relative">
                <RiCalendarLine className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" size={13} />
                <input
                  type="date"
                  value={filterDate}
                  max={toLocalDateString(today)}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="text-xs font-semibold border border-[#E5E7EB] rounded-lg pl-7 pr-2 py-1.5 bg-white text-[#374151] cursor-pointer focus:outline-none focus:border-[#0984e3] transition-colors"
                />
              </div>
              {filterDate && (
                <button
                  onClick={() => setFilterDate('')}
                  title="Clear date filter"
                  className="w-7 h-7 rounded-lg border border-[#E5E7EB] flex items-center justify-center text-[#6B7280] hover:text-red-500 hover:border-red-300 transition-all cursor-pointer"
                >
                  <RiCloseLine size={13} />
                </button>
              )}
              <button
                onClick={load}
                title="Refresh"
                className="w-7 h-7 rounded-lg border border-[#E5E7EB] flex items-center justify-center text-[#6B7280] hover:text-[#0984e3] hover:border-[#0984e3] transition-all cursor-pointer"
              >
                <RiRefreshLine size={13} />
              </button>
            </div>
          </div>
        </div>

        {/* list error */}
        {listError && (
          <div className="mx-6 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center justify-between">
            {listError}
            <button onClick={() => setListError('')} className="cursor-pointer ml-2"><RiCloseLine size={14} /></button>
          </div>
        )}

        {/* list body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="divide-y divide-[#F3F4F6]">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="p-5 space-y-2">
                  <div className="h-3 bg-[#F3F4F6] rounded w-1/3 animate-pulse" />
                  <div className="h-4 bg-[#F3F4F6] rounded w-full animate-pulse" />
                  <div className="h-3 bg-[#F3F4F6] rounded w-2/3 animate-pulse" />
                </div>
              ))}
            </div>
          ) : total === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-14 h-14 rounded-2xl bg-[#F3F4F6] flex items-center justify-center">
                <RiFileList3Line size={26} className="text-[#D1D5DB]" />
              </div>
              <div className="text-center">
                <p className="text-[#374151] text-sm font-semibold">No updates for {filterDate
                  ? new Date(filterDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                  : `${MONTH_NAMES[filterMonth - 1]} ${filterYear}`}</p>
                <p className="text-[#9CA3AF] text-xs mt-1">Submit an update from the form on the left.</p>
              </div>
            </div>
          ) : (
            <div>
              {Object.entries(grouped).map(([dateLabel, items]) => (
                <div key={dateLabel}>
                  {/* date group header */}
                  <div className="sticky top-0 z-10 px-6 py-2 bg-[#F8FAFC] border-b border-[#F3F4F6]">
                    <span className="text-xs font-semibold text-[#6B7280] flex items-center gap-1.5">
                      <RiCalendarLine size={11} />
                      {dateLabel}
                      {items.some((u) => isToday(u)) && (
                        <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-[#10b981] bg-[rgba(16,185,129,0.1)]">Today</span>
                      )}
                    </span>
                  </div>

                  <div className="divide-y divide-[#F3F4F6]">
                    {items.map((u) => {
                      const id         = u._id || u.id;
                      const isEditing  = editingId === id;
                      const isDeleting = deletingId === id;

                      return (
                        <div
                          key={id}
                          className={`px-6 py-4 transition-colors ${
                            isEditing ? 'bg-[rgba(9,132,227,0.04)] border-l-2 border-[#0984e3]' : 'hover:bg-[#FAFBFC]'
                          }`}
                        >
                          <div>
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <p className="text-[#9CA3AF] text-xs flex items-center gap-1">
                                <RiTimeLine size={11} />
                                {formatDate(u)}
                                {isToday(u) && (
                                  <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-[#10b981] bg-[rgba(16,185,129,0.1)]">Today</span>
                                )}
                                {isEditing && (
                                  <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-[#0984e3] bg-[rgba(9,132,227,0.1)]">Editing</span>
                                )}
                              </p>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  onClick={() => isEditing ? cancelEdit() : startEdit(u)}
                                  className={`p-1.5 rounded-lg transition-all cursor-pointer ${
                                    isEditing
                                      ? 'text-[#0984e3] bg-[rgba(9,132,227,0.08)]'
                                      : 'text-[#9CA3AF] hover:text-[#0984e3] hover:bg-[rgba(9,132,227,0.08)]'
                                  }`}
                                  aria-label={isEditing ? 'Cancel edit' : 'Edit'}
                                  title={isEditing ? 'Cancel editing' : 'Edit'}
                                >
                                  {isEditing ? <RiCloseLine size={13} /> : <RiEditLine size={13} />}
                                </button>
                                <button
                                  onClick={() => handleDelete(id)}
                                  disabled={isDeleting}
                                  className="p-1.5 text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer disabled:opacity-50"
                                  aria-label="Delete"
                                >
                                  {isDeleting
                                    ? <span className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin block" />
                                    : <RiDeleteBinLine size={13} />}
                                </button>
                              </div>
                            </div>
                            <UpdateSections u={u} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* pagination */}
        <Pagination page={page} total={total} onChange={setPage} />
      </div>
    </div>
  );
}
